import React, { useState, Fragment, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XCircleIcon,
  DocumentTextIcon,
  PaperClipIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface EmailTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  ticketType: 'INCIDENT' | 'PROBLEM' | 'CHANGE' | 'SERVICE_REQUEST' | 'CASE';
  ticketNumber: string;
  shortDescription: string;
}

const EmailTicketModal: React.FC<EmailTicketModalProps> = ({
  isOpen,
  onClose,
  ticketId,
  ticketType,
  ticketNumber,
  shortDescription,
}) => {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [newCcRecipient, setNewCcRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [additionalMessage, setAdditionalMessage] = useState('');
  const [includeWorkNotes, setIncludeWorkNotes] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [includeRelatedRecords, setIncludeRelatedRecords] = useState(false);
  const [includeProblemTasks, setIncludeProblemTasks] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(false);

  // Format ticket type for display
  const formatTicketType = (type: string): string => {
    switch (type) {
      case 'INCIDENT': return 'Incident';
      case 'PROBLEM': return 'Problem';
      case 'CHANGE': return 'Change Request';
      case 'SERVICE_REQUEST': return 'Service Request';
      default: return 'Case';
    }
  };

  // Strip HTML tags from text
  const stripHtml = (html: string): string => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Initialize subject and default message when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const cleanDescription = stripHtml(shortDescription);
      const truncatedDesc = cleanDescription.length > 60
        ? cleanDescription.substring(0, 60) + '...'
        : cleanDescription;
      setSubject(`[${formatTicketType(ticketType)}] ${ticketNumber} - ${truncatedDesc}`);

      // Set default additional message
      const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.firstName || user?.email || 'Team';
      const defaultMessage = `Hi,

Below is the ticket detail requested of ${formatTicketType(ticketType)}.

Ticket Number: ${ticketNumber}

Regards,
${userName}`;
      setAdditionalMessage(defaultMessage);
      setError(null);
    }
  }, [isOpen, ticketType, ticketNumber, shortDescription, user]);

  // Reset form when modal closes
  const handleClose = useCallback(() => {
    setRecipients([]);
    setCcRecipients([]);
    setNewRecipient('');
    setNewCcRecipient('');
    setSubject('');
    setAdditionalMessage('');
    setIncludeWorkNotes(false);
    setIncludeAttachments(false);
    setIncludeRelatedRecords(false);
    setIncludeProblemTasks(true);
    setShowCc(false);
    setError(null);
    onClose();
  }, [onClose]);

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Add recipient
  const handleAddRecipient = (e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent) => {
    if (('key' in e && e.key !== 'Enter') && e.type !== 'click') return;
    e.preventDefault();

    const email = newRecipient.trim();
    if (email && isValidEmail(email) && !recipients.includes(email)) {
      setRecipients([...recipients, email]);
      setNewRecipient('');
      setError(null);
    } else if (email && !isValidEmail(email)) {
      setError('Please enter a valid email address');
    }
  };

  // Add CC recipient
  const handleAddCcRecipient = (e: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent) => {
    if (('key' in e && e.key !== 'Enter') && e.type !== 'click') return;
    e.preventDefault();

    const email = newCcRecipient.trim();
    if (email && isValidEmail(email) && !ccRecipients.includes(email)) {
      setCcRecipients([...ccRecipients, email]);
      setNewCcRecipient('');
      setError(null);
    } else if (email && !isValidEmail(email)) {
      setError('Please enter a valid email address');
    }
  };

  // Remove recipient
  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  // Remove CC recipient
  const handleRemoveCcRecipient = (email: string) => {
    setCcRecipients(ccRecipients.filter(r => r !== email));
  };

  // Send email
  const handleSendEmail = async () => {
    if (recipients.length === 0) {
      setError('Please add at least one recipient');
      return;
    }

    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await api.post(`/cases/${ticketId}/send-email`, {
        to: recipients,
        cc: ccRecipients.length > 0 ? ccRecipients : undefined,
        subject: subject.trim(),
        additionalMessage: additionalMessage.trim() || undefined,
        includeAttachments,
        includeWorkNotes,
        includeRelatedRecords,
        includeProblemTasks: ticketType === 'PROBLEM' ? includeProblemTasks : false,
      });

      if (response.data.success) {
        // Show success and close modal
        handleClose();
        // You could also show a toast notification here
        alert(`Email sent successfully to ${response.data.emailsSent} recipient(s)`);
      } else {
        setError(response.data.message || 'Failed to send email');
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError(err.response?.data?.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-6 w-6 text-indigo-600" />
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Email Ticket Details
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                  {/* Error message */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Ticket Info */}
                  <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                    <p className="text-sm font-medium text-indigo-700">
                      {formatTicketType(ticketType)}: {ticketNumber}
                    </p>
                    <p className="text-sm text-indigo-600 truncate">{stripHtml(shortDescription)}</p>
                  </div>

                  {/* To Recipients */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {recipients.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                        >
                          {email}
                          <button
                            onClick={() => handleRemoveRecipient(email)}
                            className="ml-1.5 hover:text-indigo-600"
                          >
                            <XCircleIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        onKeyDown={handleAddRecipient}
                        placeholder="Enter email and press Enter"
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                      <button
                        onClick={handleAddRecipient}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {!showCc && (
                      <button
                        onClick={() => setShowCc(true)}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        + Add CC
                      </button>
                    )}
                  </div>

                  {/* CC Recipients */}
                  {showCc && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CC
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {ccRecipients.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {email}
                            <button
                              onClick={() => handleRemoveCcRecipient(email)}
                              className="ml-1.5 hover:text-gray-600"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={newCcRecipient}
                          onChange={(e) => setNewCcRecipient(e.target.value)}
                          onKeyDown={handleAddCcRecipient}
                          placeholder="Enter email and press Enter"
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <button
                          onClick={handleAddCcRecipient}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Subject */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Additional Message */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Message <span className="text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      value={additionalMessage}
                      onChange={(e) => setAdditionalMessage(e.target.value)}
                      rows={3}
                      placeholder="Add a personal message to include at the top of the email..."
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Include Options */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Include in Email
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeWorkNotes}
                          onChange={(e) => setIncludeWorkNotes(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 ml-2 mr-2" />
                        <span className="text-sm text-gray-700">Work Notes</span>
                      </label>
                      <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeAttachments}
                          onChange={(e) => setIncludeAttachments(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <PaperClipIcon className="h-5 w-5 text-gray-400 ml-2 mr-2" />
                        <span className="text-sm text-gray-700">Attachments</span>
                      </label>
                      <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeRelatedRecords}
                          onChange={(e) => setIncludeRelatedRecords(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <LinkIcon className="h-5 w-5 text-gray-400 ml-2 mr-2" />
                        <span className="text-sm text-gray-700">Related Records</span>
                      </label>
                      {ticketType === 'PROBLEM' && (
                        <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeProblemTasks}
                            onChange={(e) => setIncludeProblemTasks(e.target.checked)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 ml-2 mr-2" />
                          <span className="text-sm text-gray-700">Problem Tasks</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Attachment Warning */}
                  {includeAttachments && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-700">
                        Attachments larger than 10MB total will be excluded. Recipients can download them from the ticket.
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={handleClose}
                    disabled={isSending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={isSending || recipients.length === 0}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailTicketModal;
